import {
  getDifferetsIdFromArray,
  createAQueryParam,
  createQueryParams,
} from '../../../utils/utils';
import { SURVEY_BASE_URL } from '../../../constants';
import LocalStorageService from '../../LocalStorage/LocalStorage.service';

const QueryParamNames = {
  province: 'provincia',
  zipCode: 'codigoPostal',
  speciality: 'especialidad',
};

export const DEFAULT_SPECIALITY_NUMBER = '10000';

const SurveyHomeService = {
  surveyURL: `${SURVEY_BASE_URL}/jfe/form/SV_9LJzu6p7hdz6Bfg`,
  getSpecialityNumber: (practitioner) => practitioner.nroespe ?? DEFAULT_SPECIALITY_NUMBER,
  getOwnCentre: (practitioner) => practitioner.Centro_Propio,
  getSpecialityNumberAndOwnCentreFromResponse: (response) => {
    if (response?.docs && Array.isArray(response?.docs)) {
      return response.docs.map((practitioner, idx) => {
        return {
          id: idx,
          specialityNumber: SurveyHomeService.getSpecialityNumber(practitioner),
          ownCentre: SurveyHomeService.getOwnCentre(practitioner),
        };
      });
    }
    return {};
  },
  getZipCode: (address) => address.codigoPostal,
  getProvinceId: (address) => address.provincia.id,
  getAddressData: (response) => {
    if (response && Array.isArray(response)) {
      return response.map((item, idx) => {
        return {
          id: idx,
          zipCode: SurveyHomeService.getZipCode(item.domicilio),
          provinceId: SurveyHomeService.getProvinceId(item.domicilio),
        };
      });
    }
    return {};
  },
  getProvincesToUrl: (addresses) => {
    const provincesId = getDifferetsIdFromArray(addresses, 'provinceId');
    return createQueryParams(provincesId, QueryParamNames.province);
  },
  getZipCodesToUrl: (addresses) => {
    const zipCodes = getDifferetsIdFromArray(addresses, 'zipCode');
    return createQueryParams(zipCodes, QueryParamNames.zipCode);
  },
  getSpecialitiesToUrl: (specialities) => {
    let specialitiesNumber = [DEFAULT_SPECIALITY_NUMBER];
    if (Array.isArray(specialities) && specialities.length > 0) {
      specialitiesNumber = getDifferetsIdFromArray(specialities, 'specialityNumber');
    }
    return createQueryParams(specialitiesNumber, QueryParamNames.speciality);
  },
  hasOwnCentre: (specialities) => {
    return Array.isArray(specialities)
      ? specialities.some((speciality) => speciality.ownCentre)
      : false;
  },
  createQueryWithExternalData: (externalData) => {
    const ownCentre = SurveyHomeService.hasOwnCentre(externalData.especialidades);
    const externalDataUrls = [
      SurveyHomeService.getProvincesToUrl(externalData.direcciones),
      SurveyHomeService.getZipCodesToUrl(externalData.direcciones),
      SurveyHomeService.getSpecialitiesToUrl(externalData.especialidades),
    ];
    const externalUrl = externalDataUrls.map((value) => value).join('&');

    return `${externalUrl}&centropropio=${ownCentre}`;
  },
  getQueryFromInternalData: (queryParams) => {
    let internalData = [];
    for (let [key, value] of Object.entries(queryParams)) {
      if (key !== 'direcciones' && key !== 'especialidades') {
        internalData.push({ key, value });
      }
    }
    return internalData.map(({ key, value }) => createAQueryParam(key, value)).join('&');
  },
  createQueryFromObject: (queryParams) => {
    const externalData = {
      direcciones: queryParams.direcciones,
      especialidades: queryParams.especialidades,
    };
    const externalUrl = SurveyHomeService.createQueryWithExternalData(externalData);
    const internalUrl = SurveyHomeService.getQueryFromInternalData(queryParams);

    return `${internalUrl}&${externalUrl}`;
  },
  isInAMBA: (managmentUnitResponse) => {
    let managmentUnitOutcome = 'A';
    let unidadesDeGestion = [];

    if (managmentUnitResponse && managmentUnitResponse.length > 0) {
      managmentUnitOutcome = 'A';
      managmentUnitResponse.forEach((unidades) => unidadesDeGestion.push(unidades.unidadGestion));
      // para simular:
      // unidadesDeGestion = [ 6 ]
      // Solo 6
      if (unidadesDeGestion.length === 1 && unidadesDeGestion.includes(6))
        managmentUnitOutcome = 'I';
      // Si vienen varias unidades de gestión, es interior cuando trae 6 y cualquier otra distinta de 5.
      if (
        unidadesDeGestion.length > 1 &&
        unidadesDeGestion.includes(6) &&
        !unidadesDeGestion.includes(5)
      )
        managmentUnitOutcome = 'I';
    }

    return managmentUnitOutcome === 'A';
  },
  isDirectOrTeamProvider: (userDetail) => ['P', 'E'].includes(userDetail?.tipoPrestador?.id),
  userIsAuth: (isAuth) => isAuth,
  showButtonSurvey: (managmentUnit, userDetail, isAuth) =>
    SurveyHomeService.isInAMBA(managmentUnit) &&
    SurveyHomeService.isDirectOrTeamProvider(userDetail) &&
    isAuth,
  setLastQualtricsSurveyResponse: (value) =>
    LocalStorageService.set('lastHomeSurveyResponse', value),
  getLastQualtricsSurveyResponse: () => LocalStorageService.get('lastHomeSurveyResponse'),
  setLastQualtricsSurveyClosed: (value) => LocalStorageService.set('lastHomeSurveyClosed', value),
  getLastQualtricsSurveyClosed: () => LocalStorageService.get('lastHomeSurveyClosed'),
};

export default SurveyHomeService;
